import { create } from '@bufbuild/protobuf'
import { createConnectQueryKey, useMutation, useQuery, useTransport } from '@connectrpc/connect-query'
import { useQueryClient } from '@tanstack/react-query'
import { useMemo } from 'react'

import {
  acceptEmailThreadAddressSuggestion,
  dismissEmailThreadAddressSuggestion,
  getContact,
  listEmailThreadAddressSuggestions,
} from '@/gen/cortado/v1/cortado-CortadoService_connectquery'
import { ListEmailThreadAddressSuggestionsRequestSchema } from '@/gen/cortado/v1/email_conversations_pb'

/**
 * Addresses on a conversation that a linked contact may also use (CPRO-788):
 * sent under that contact's name and on no contact yet. Needs View
 * Communications and the EmailInbox add-on.
 */
export function useEmailThreadAddressSuggestions(
  organizationId: string,
  threadId: string,
  options: { enabled?: boolean } = {},
) {
  const request = useMemo(
    () => create(ListEmailThreadAddressSuggestionsRequestSchema, { organizationId, threadId }),
    [organizationId, threadId],
  )
  const query = useQuery(listEmailThreadAddressSuggestions, request, {
    enabled: options.enabled !== false && !!organizationId && !!threadId,
    staleTime: 30 * 1000,
  })
  return {
    suggestions: query.data?.suggestions ?? [],
    hasLoaded: query.isSuccess || !!query.data,
  }
}

function useInvalidateSuggestions() {
  const queryClient = useQueryClient()
  const transport = useTransport()
  return () =>
    queryClient.invalidateQueries({
      queryKey: createConnectQueryKey({ schema: listEmailThreadAddressSuggestions, transport, cardinality: undefined }),
    })
}

/** Adds a suggested address to its contact, then refreshes the contact. */
export function useAcceptEmailThreadAddressSuggestion() {
  const queryClient = useQueryClient()
  const transport = useTransport()
  const invalidateSuggestions = useInvalidateSuggestions()
  return useMutation(acceptEmailThreadAddressSuggestion, {
    onSuccess: () => {
      void invalidateSuggestions()
      void queryClient.invalidateQueries({
        queryKey: createConnectQueryKey({ schema: getContact, transport, cardinality: undefined }),
      })
    },
  })
}

/** Hides a suggestion on a conversation for the current user. */
export function useDismissEmailThreadAddressSuggestion() {
  const invalidateSuggestions = useInvalidateSuggestions()
  return useMutation(dismissEmailThreadAddressSuggestion, {
    onSuccess: () => {
      void invalidateSuggestions()
    },
  })
}
